import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { fetchPosts, setCurrentCategory, prependNewPosts } from './features/posts/postsSlice';
import Categories from './components/Categories';
import PostsList from './components/PostsList';
import Header from './components/Header';
import Footer from './components/Footer';
import { motion, AnimatePresence } from 'framer-motion';

const REFRESH_INTERVAL = 60000;

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #f6f7f8;
  padding-top: 72px;

  @media (max-width: 768px) {
    padding-top: 108px;
  }
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
  }
`;

const ErrorBanner = styled(motion.div)`
  background-color: #ff4500;
  color: white;
  padding: 0.75rem 1rem;
  text-align: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  font-size: 0.875rem;
  font-weight: 500;
`;

const NewPostsBanner = styled(motion.button)`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1500;
  background: #ff4500;
  color: white;
  border: none;
  border-radius: 24px;
  padding: 0.55rem 1.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 69, 0, 0.35);
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    background: #e03d00;
  }

  @media (max-width: 768px) {
    top: 120px;
    font-size: 0.8rem;
    padding: 0.5rem 1.1rem;
  }
`;

function App() {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [pendingPosts, setPendingPosts] = useState([]);
  const { currentCategory, items } = useSelector(state => state.posts);
  const intervalRef = useRef(null);
  const currentItemIds = useRef(new Set());

  useEffect(() => {
    const saved = localStorage.getItem('redcatch_category');
    if (saved && saved !== currentCategory) {
      dispatch(setCurrentCategory(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('redcatch_category', currentCategory);

    const load = async () => {
      try {
        await dispatch(fetchPosts({ category: currentCategory })).unwrap();
      } catch (err) {
        setError('Failed to load posts. Please try again later.');
        setTimeout(() => setError(null), 5000);
      }
    };
    load();
  }, [currentCategory, dispatch]);

  useEffect(() => {
    currentItemIds.current = new Set(items.map(p => p.id));
  }, [items]);

  const checkForNewPosts = useCallback(async () => {
    try {
      const result = await dispatch(fetchPosts({ category: currentCategory, silent: true })).unwrap();
      const fresh = result.posts.filter(p => !currentItemIds.current.has(p.id));
      if (fresh.length > 0) {
        setPendingPosts(fresh);
        setNewCount(fresh.length);
      }
    } catch {}
  }, [currentCategory, dispatch]);

  useEffect(() => {
    setNewCount(0);
    setPendingPosts([]);

    intervalRef.current = setInterval(checkForNewPosts, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [currentCategory, checkForNewPosts]);

  const handleShowNew = useCallback(() => {
    dispatch(prependNewPosts(pendingPosts));
    setNewCount(0);
    setPendingPosts([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dispatch, pendingPosts]);

  return (
    <AppContainer>
      <AnimatePresence>
        {error && (
          <ErrorBanner
            initial={{ y: -48 }}
            animate={{ y: 0 }}
            exit={{ y: -48 }}
            transition={{ type: 'spring', stiffness: 140, damping: 20 }}
          >
            {error}
          </ErrorBanner>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {newCount > 0 && (
          <NewPostsBanner
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={handleShowNew}
          >
            ↑ {newCount} new {newCount === 1 ? 'post' : 'posts'}
          </NewPostsBanner>
        )}
      </AnimatePresence>

      <Header />

      <MainContent>
        <Categories />
        <PostsList />
      </MainContent>

      <Footer />
    </AppContainer>
  );
}

export default React.memo(App);